// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import * as NameplateCommands from '@app/features/user/commands/NameplateCommands';
import {registerCosmeticPoster} from '@app/features/user/state/CosmeticPosterRegistry';
import {rewriteLegacyApiHostUrl} from '@app/features/user/utils/LegacyApiHostRewrite';
import type {NameplateCatalogEntryResponse} from '@fluxer/schema/src/domains/user/NameplateSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('NameplateCatalog');

let inflightLoad: Promise<void> | null = null;

class NameplateCatalog {
	nameplates: Array<NameplateCatalogEntryResponse> = [];
	loaded = false;
	loading = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getById(id: string | null | undefined): NameplateCatalogEntryResponse | undefined {
		if (!id) {
			return undefined;
		}
		return this.nameplates.find((entry) => entry.id === id);
	}

	async load(): Promise<void> {
		if (this.loaded && this.nameplates.length > 0) {
			return;
		}
		if (inflightLoad) {
			return inflightLoad;
		}
		inflightLoad = this.fetchCatalog();
		try {
			await inflightLoad;
		} finally {
			inflightLoad = null;
		}
	}

	private async fetchCatalog(): Promise<void> {
		this.loading = true;
		try {
			const response = await NameplateCommands.fetchNameplateCatalog();
			runInAction(() => {
				this.nameplates = response.nameplates.map((entry) => ({
					...entry,
					image: rewriteLegacyApiHostUrl(entry.image),
					...(entry.poster ? {poster: rewriteLegacyApiHostUrl(entry.poster)} : {}),
				}));
				for (const entry of this.nameplates) registerCosmeticPoster(entry.image, entry.poster);
				this.loaded = true;
			});
		} catch (error) {
			logger.warn('Failed to load nameplate catalog', error);
			runInAction(() => {
				this.nameplates = [];
				this.loaded = true;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export default new NameplateCatalog();
